import { useEffect, useState } from 'react';
import { api, CaigenSettings, DigestPreview } from '../../lib/api';
import { FocusRootTimer } from '../focus/FocusRootTimer';
import { useFocusTimer } from '../focus/focus-context';

export function AccountPreferences({ initial, writeAllowed, onChanged }: { initial: CaigenSettings; writeAllowed: boolean; onChanged: (settings: CaigenSettings) => void }) {
  const focus = useFocusTimer();
  const [settings, setSettings] = useState<CaigenSettings>(initial);
  const [preview, setPreview] = useState<DigestPreview>();
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setSettings(initial); }, [initial]);

  const save = async (key: 'dailyDigest' | 'pomodoro', value: boolean) => {
    if (!writeAllowed || busy) return;
    setBusy(true); setMessage('');
    try {
      const result = await api<{ data: { settings: CaigenSettings } }>('/api/v1/settings', { method: 'PUT', body: JSON.stringify({ [key]: value }) });
      setSettings(result.data.settings); onChanged(result.data.settings);
      setMessage(value ? `${key === 'dailyDigest' ? '每日信报' : '菜根钟'}已开启。` : `${key === 'dailyDigest' ? '每日信报' : '菜根钟'}已关闭。`);
    } catch (error) { setMessage(error instanceof Error ? error.message : '偏好保存失败'); }
    finally { setBusy(false); }
  };

  const loadPreview = async () => {
    setBusy(true); setMessage('');
    try {
      const result = await api<{ data: DigestPreview }>('/api/v1/digest/preview?limit=5');
      setPreview(result.data);
      setMessage(result.data.items?.length ? '已按当前画像生成预览。' : '当前没有可投递的信息。');
    } catch (error) { setMessage(error instanceof Error ? error.message : '信报预览失败'); }
    finally { setBusy(false); }
  };

  return <section className="paper-card preference-center" id="preference-center">
    <header><div><small>RHYTHM · PERSONAL SETTINGS</small><h3>日常节律</h3><p>把推送与专注做成可见、可停的个人节律，不让后台设置悄悄运行。</p></div><span>{settings.dailyDigest || settings.pomodoro ? '已有 1 个以上节律' : '均未开启'}</span></header>
    <div className="preference-grid">
      <article className="digest-preference">
        <div className="preference-title"><i>信</i><div><small>DAILY DIGEST</small><h4>每日信报</h4></div><button type="button" aria-pressed={Boolean(settings.dailyDigest)} disabled={!writeAllowed || busy} onClick={() => void save('dailyDigest', !settings.dailyDigest)}><span className={settings.dailyDigest ? 'on' : ''} /></button></div>
        <p>每天把与你学院、年级和订阅主题有关的 5 条信息汇成一张收件箱卡片。</p>
        <button className="quiet-button" type="button" disabled={busy} onClick={() => void loadPreview()}>预览今天的信报</button>
        {preview ? <div className="digest-preview">{preview.items?.slice(0, 5).map((item, index) => <a key={`${item.id || item.title}-${index}`} href={item.url || undefined} target={item.url ? '_blank' : undefined} rel="noreferrer"><span>{index + 1}</span><p><b>{item.title}</b><small>{item.sourceName || item.source || item.category || '校园信库'}</small></p></a>) || null}</div> : null}
      </article>
      <article className={`pomodoro-preference${settings.pomodoro ? ' enabled' : ''}`}>
        <div className="preference-title"><i>专</i><div><small>CAIGEN FOCUS</small><h4>菜根钟</h4></div><button type="button" aria-pressed={Boolean(settings.pomodoro)} disabled={!writeAllowed || busy} onClick={() => void save('pomodoro', !settings.pomodoro)}><span className={settings.pomodoro ? 'on' : ''} /></button></div>
        <FocusRootTimer compact progress={focus.progress} remaining={focus.remaining} durationSeconds={focus.durationSeconds} mode={focus.mode} />
        <div className="pomodoro-account-actions"><a className="primary-button" href="/?view=focus">{focus.running || focus.progress > 0 ? '进入专注' : '打开专注页'}</a>{focus.running ? <button className="quiet-button" type="button" onClick={focus.pause}>暂停</button> : null}</div>
        <p className="pomodoro-hint">今日 {focus.todayMinutes} 分钟 · {focus.todaySessions} 次；状态仅保存在当前浏览器。</p>
      </article>
    </div>
    {message ? <p className="preference-message" role="status">{message}</p> : null}
  </section>;
}
